import { useSnackbar } from 'notistack';
import React, { ReactNode, useCallback, useState } from 'react';
import { FlightCalculatorForm, FlightCalculatorFormValuesProps } from 'src/@types/flightCalculatorSettings';
import {
  getFlightCalculatorSettingsByMerchantCodeApi,
  saveFlightCalculatorSettingsApi,
  updateFlightCalculatorSettingsApi,
} from 'src/api/flightCalculatorSettingsApi';

export type FlightCalculatorSettingsState = {
  getFlightCalculatorSettings: (merchantCode: string) => void;
  saveFlightCalculatorSettings: (merchantCode: string, settings: FlightCalculatorFormValuesProps) => void;
  currentSettings: FlightCalculatorForm | null;
  isLoadingCurrentSettings: boolean;
  updateFlightCalculatorSettings: (id: string, settings: FlightCalculatorFormValuesProps) => void;
};

export const FlightCalculatorSettingsContext = React.createContext<FlightCalculatorSettingsState>({
  getFlightCalculatorSettings: () => {},
  saveFlightCalculatorSettings: () => {},
  currentSettings: null,
  isLoadingCurrentSettings: true,
  updateFlightCalculatorSettings: () => {},
});

type FlightCalculatorSettingsProviderProps = {
  children: ReactNode;
};

export const FlightCalculatorSettingsContextProvider = ({ children }: FlightCalculatorSettingsProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [currentSettings, setCurrentSettings] = useState<FlightCalculatorForm | null>(null);
  const [isLoadingCurrentSettings, setIsLoadingCurrentSettings] = useState<boolean>(true);

  const getFlightCalculatorSettings = useCallback((merchantCode: string) => {
    setIsLoadingCurrentSettings(true);
    
    getFlightCalculatorSettingsByMerchantCodeApi(merchantCode)
      .then((response) => {
        setCurrentSettings(response.data);
        enqueueSnackbar('Configuration is loaded!', { variant: "info" });
      })
      .catch((response) => {
        console.error(response);
      })
      .finally(() => {
        setIsLoadingCurrentSettings(false);
      })
  },[enqueueSnackbar]);

  const saveFlightCalculatorSettings = useCallback((merchantCode: string, settings: FlightCalculatorFormValuesProps) => {
    saveFlightCalculatorSettingsApi(merchantCode, settings)
      .then((response) => {
        enqueueSnackbar('Configuration is saved!', { variant: "success" });
        setCurrentSettings(response.data);
      })
      .catch((response) => {
        console.error(response);
        enqueueSnackbar('Ops! Something went wrong!', { variant: "error" });
      })
  }, [enqueueSnackbar]);

  const updateFlightCalculatorSettings = useCallback((id: string, settings: FlightCalculatorFormValuesProps) => {
    updateFlightCalculatorSettingsApi(id, settings)
      .then((response) => {
        enqueueSnackbar('Configuration is updated!', { variant: "success" });
        setCurrentSettings({ ...response.data, id });
      })
      .catch((response) => {
        console.error(response);
        enqueueSnackbar('Ops! Something went wrong!', { variant: "error" });
      })
  },[enqueueSnackbar]);

  return (
    <FlightCalculatorSettingsContext.Provider
      value={{
        getFlightCalculatorSettings,
        saveFlightCalculatorSettings,
        isLoadingCurrentSettings,
        currentSettings,
        updateFlightCalculatorSettings
      }}
    >
      {children}
    </FlightCalculatorSettingsContext.Provider>
  );
};

